// src/services/grammarProgressService.js
import { db } from '../firebase';
import {
  doc,
  setDoc,
  getDoc,
  updateDoc,
  serverTimestamp,
  arrayUnion,
  arrayRemove,
  increment
} from 'firebase/firestore';

/**
 * Enhanced Grammar Progress Service
 * Tracks progress, mistakes, and exercise continuation
 */
class GrammarProgressService {
  /**
   * Initialize grammar progress for a user
   */
  async initializeProgress(userId, level = 'B1') {
    try {
      const progressRef = doc(db, 'users', userId, 'grammarProgress', level.toLowerCase());
      let totalExercises = 0;
      try {
        const levelLower = level.toLowerCase();
        const practiceResponse = await fetch(`/data/grammar/grammar-${levelLower}.json`);
        if (practiceResponse.ok) {
          const practiceData = await practiceResponse.json();
          totalExercises = practiceData.exercises?.length || 0;
        }
      } catch (e) {
        console.error(`Could not fetch practice data for level ${level} to get total count`, e);
      }

      const initialProgress = {
        level: level,
        currentPillar: null,
        currentExerciseIndex: 0,
        lastAccessedAt: serverTimestamp(),
        createdAt: serverTimestamp(),
        pillars: {},
        stats: {
          totalExercises: totalExercises,
          completedExercises: 0,
          correctAnswers: 0,
          incorrectAnswers: 0,
          totalTimeSpent: 0,
          averageAccuracy: 0,
          streakDays: 0,
          lastStudyDate: null
        },
        mistakes: {
          exercises: [],
          details: {},
          reviewQueue: [],
          maxMistakes: 50
        },
        bookmarks: []
      };

      await setDoc(progressRef, initialProgress);
      return initialProgress;
    } catch (error) {
      console.error('Error initializing grammar progress:', error);
      throw error;
    }
  }

  async toggleBookmark(userId, level, bookmark) {
    if (!userId || !level || !bookmark?.exerciseId) return false;

    try {
      const progressRef = doc(db, 'users', userId, 'grammarProgress', level.toLowerCase());
      const progressDoc = await getDoc(progressRef);

      if (progressDoc.exists()) {
        const bookmarks = progressDoc.data().bookmarks || [];
        const existingBookmark = bookmarks.find(b => b.exerciseId === bookmark.exerciseId);

        if (existingBookmark) {
          await updateDoc(progressRef, {
            bookmarks: arrayRemove(existingBookmark)
          });
        } else {
          await updateDoc(progressRef, {
            bookmarks: arrayUnion(bookmark)
          });
        }
        return true;
      }
      return false;
    } catch (error) {
      console.error('Error toggling bookmark:', error);
      return false;
    }
  }

  /**
   * Get user's grammar progress for a specific level
   */
  async getUserProgress(userId, level = 'B1') {
    try {
      const progressRef = doc(db, 'users', userId, 'grammarProgress', level.toLowerCase());
      const progressDoc = await getDoc(progressRef);

      if (progressDoc.exists()) {
        return progressDoc.data();
      } else {
        return await this.initializeProgress(userId, level);
      }
    } catch (error) {
      console.error('Error getting grammar progress:', error);
      return null;
    }
  }

  /**
   * Save current position (pillar and exercise index)
   */
  async saveCurrentPosition(userId, level, pillarId, exerciseIndex) {
    try {
      const progressRef = doc(db, 'users', userId, 'grammarProgress', level.toLowerCase());

      // Verify the document exists first
      const progressDoc = await getDoc(progressRef);
      if (!progressDoc.exists()) {
        await this.initializeProgress(userId, level);
      }

      const updateData = {
        currentPillar: pillarId,
        currentExerciseIndex: exerciseIndex,
        lastAccessedAt: serverTimestamp()
      };

      await updateDoc(progressRef, updateData);
      return true;
    } catch (error) {
      console.error('Error saving position:', error);
      return false;
    }
  }
detectExerciseContext(grammarData, pillarId, exerciseId) {
    try {
      const pillar = grammarData?.pillars?.find(p => p.id === pillarId);
      if (!pillar) return null;

      // Check B2/C1 structure (categories)
      if (pillar.explanation?.categories) {
        for (const category of pillar.explanation.categories) {
          if (category.practice) {
            for (const practiceLevel of category.practice) {
              const hasExercise = practiceLevel.tasks?.some(task => task.id === exerciseId);
              if (hasExercise) {
                return {
                  categoryId: category.id || category.name?.toLowerCase().replace(/\s+/g, '_'),
                  categoryName: category.name,
                  practiceLevel: practiceLevel.level,
                  practiceLevelName: practiceLevel.level,
                  structureType: 'categories'
                };
              }
            }
          }
        }
      }

      // Check A2/B1 structure (practice levels)
      if (pillar.practice) {
        for (const practiceLevel of pillar.practice) {
          const hasExercise = practiceLevel.tasks?.some(task => task.id === exerciseId);
          if (hasExercise) {
            return {
              practiceLevel: practiceLevel.level,
              practiceLevelName: practiceLevel.level,
              categoryId: null,
              categoryName: null,
              structureType: 'practice'
            };
          }
        }
      }

      return null;
    } catch (error) {
      console.error('Error detecting exercise context:', error);
      return null;
    }
  }
  /**
   * Update exercise completion with mistake tracking
   */
  async updateExerciseCompletion(userId, level, pillarId, exerciseId, result) {
    try {
      const progressRef = doc(db, 'users', userId, 'grammarProgress', level.toLowerCase());
      const currentProgress = await this.getUserProgress(userId, level);

      if (!currentProgress) throw new Error('Progress not found for user.');

      // Initialize pillar structure if it doesn't exist
      if (!currentProgress.pillars[pillarId]) {
        currentProgress.pillars[pillarId] = { exercises: {} };
      }

      if (!currentProgress.pillars[pillarId].exercises) {
        currentProgress.pillars[pillarId].exercises = {};
      }

      const wasAlreadyCompleted = !!currentProgress.pillars[pillarId]?.exercises[exerciseId]?.completed;
      const wasAlreadyCorrect = !!currentProgress.pillars[pillarId]?.exercises[exerciseId]?.correct;

      // Prepare updates
      const exerciseUpdatePath = `pillars.${pillarId}.exercises.${exerciseId}`;
      const pillarUpdatePath = `pillars.${pillarId}`;

      const updates = {
        [`${exerciseUpdatePath}.completed`]: true,
        [`${exerciseUpdatePath}.correct`]: result.isCorrect,
        [`${exerciseUpdatePath}.attempts`]: increment(1),
        [`${exerciseUpdatePath}.lastAttempt`]: serverTimestamp(),
        [`${exerciseUpdatePath}.timeSpent`]: increment(result.timeSpent || 0),
        [`${pillarUpdatePath}.lastAccessed`]: serverTimestamp(),
        'stats.totalTimeSpent': increment(result.timeSpent || 0),
      };

      // Handle statistics
      if (!wasAlreadyCompleted) {
        updates['stats.completedExercises'] = increment(1);
        if (result.isCorrect) {
          updates['stats.correctAnswers'] = increment(1);
        } else {
          updates['stats.incorrectAnswers'] = increment(1);
        }
      } else if (result.isCorrect && !wasAlreadyCorrect) {
        updates['stats.correctAnswers'] = increment(1);
        updates['stats.incorrectAnswers'] = increment(-1);
      } else if (!result.isCorrect && wasAlreadyCorrect) {
        updates['stats.correctAnswers'] = increment(-1);
        updates['stats.incorrectAnswers'] = increment(1);
      }

      // Handle mistakes
      const mistakeId = `${pillarId}_${exerciseId}`;
      if (!result.isCorrect) {
        // Enhanced mistake structure with context
        const mistakeData = {
          pillarId,
          exerciseId,
          userAnswer: result.userAnswer,
          correctAnswer: result.correctAnswer,
          timestamp: serverTimestamp(),
          reviewed: false,
          reviewCount: (currentProgress.mistakes?.details[mistakeId]?.reviewCount || 0),
        };

        // Add context if available (categoryId, practiceLevel, etc.)
        if (result.context) {
          mistakeData.categoryId = result.context.categoryId || null;
          mistakeData.categoryName = result.context.categoryName || null;
          mistakeData.practiceLevel = result.context.practiceLevel || null;
          mistakeData.practiceLevelName = result.context.practiceLevelName || null;
          mistakeData.structureType = result.context.structureType || null;
        }

        updates[`mistakes.details.${mistakeId}`] = mistakeData;

        const isInReviewQueue = currentProgress.mistakes?.reviewQueue?.includes(mistakeId) || false;
        if (!isInReviewQueue) {
          updates['mistakes.reviewQueue'] = arrayUnion(mistakeId);
        }
      } else if (result.isCorrect && currentProgress.mistakes?.details[mistakeId]) {
        updates['mistakes.reviewQueue'] = arrayRemove(mistakeId);
        updates[`mistakes.details.${mistakeId}.reviewed`] = true;
        updates[`mistakes.details.${mistakeId}.correctedAt`] = serverTimestamp();
      }

      // Calculate and update average accuracy
      const newCompletedCount = (currentProgress.stats?.completedExercises || 0) + (wasAlreadyCompleted ? 0 : 1);
      const newCorrectCount = (currentProgress.stats?.correctAnswers || 0) +
                             (result.isCorrect && !wasAlreadyCompleted ? 1 :
                              result.isCorrect && !wasAlreadyCorrect ? 1 : 0);
      const newAccuracy = newCompletedCount > 0 ? Math.round((newCorrectCount / newCompletedCount) * 100) : 0;
      updates['stats.averageAccuracy'] = newAccuracy;

      await updateDoc(progressRef, updates);
      return true;

    } catch (error) {
      console.error('Error updating exercise completion:', error);
      return false;
    }
  }

  /**
   * Get mistakes for review
   */
  async getMistakes(userId, level, pillarId = null) {
    try {
      const progress = await this.getUserProgress(userId, level);

      if (!progress || !progress.mistakes) {
        return [];
      }

      let mistakes = progress.mistakes.reviewQueue || [];

      if (pillarId) {
        mistakes = mistakes.filter(id => id.startsWith(`${pillarId}_`));
      }

      return mistakes.map(id => ({
        id,
        ...progress.mistakes.details[id]
      }));
    } catch (error) {
      console.error('Error getting mistakes:', error);
      return [];
    }
  }
async getMistakesGrouped(userId, level, currentContext = null) {
    try {
      const progress = await this.getUserProgress(userId, level);

      if (!progress || !progress.mistakes || !progress.mistakes.reviewQueue) {
        return {
          currentContext: [],
          samePillar: {},
          otherPillars: {},
          total: 0
        };
      }

      const allMistakes = progress.mistakes.reviewQueue.map(id => ({
        id,
        ...progress.mistakes.details[id]
      }));

      const result = {
        currentContext: [],
        samePillar: {},
        otherPillars: {},
        total: allMistakes.length
      };

      // If no current context, return flat structure
      if (!currentContext || !currentContext.pillarId) {
        result.otherPillars = this.groupMistakesByPillar(allMistakes);
        return result;
      }

      // Sort mistakes by context
      for (const mistake of allMistakes) {
        const isCurrentPillar = mistake.pillarId === currentContext.pillarId;
        const isCurrentCategory = currentContext.categoryId &&
                                  mistake.categoryId === currentContext.categoryId;
        const isCurrentPracticeLevel = currentContext.practiceLevel &&
                                       mistake.practiceLevel === currentContext.practiceLevel;

        // PRIORITY 1: Current exact context
        if (isCurrentPillar && (isCurrentCategory || isCurrentPracticeLevel)) {
          result.currentContext.push(mistake);
        }
        // PRIORITY 2: Same pillar, different category/level
        else if (isCurrentPillar) {
          const groupKey = mistake.categoryId || mistake.practiceLevel || 'other';
          const groupName = mistake.categoryName || mistake.practiceLevelName || 'Other';

          if (!result.samePillar[groupKey]) {
            result.samePillar[groupKey] = {
              name: groupName,
              mistakes: []
            };
          }
          result.samePillar[groupKey].mistakes.push(mistake);
        }
        // PRIORITY 3: Other pillars
        else {
          if (!result.otherPillars[mistake.pillarId]) {
            result.otherPillars[mistake.pillarId] = {
              name: mistake.pillarId,
              mistakes: []
            };
          }
          result.otherPillars[mistake.pillarId].mistakes.push(mistake);
        }
      }

      return result;
    } catch (error) {
      console.error('Error getting grouped mistakes:', error);
      return {
        currentContext: [],
        samePillar: {},
        otherPillars: {},
        total: 0
      };
    }
  }

  /**
   * Helper: Group mistakes by pillar
   */
  groupMistakesByPillar(mistakes) {
    const grouped = {};

    for (const mistake of mistakes) {
      if (!grouped[mistake.pillarId]) {
        grouped[mistake.pillarId] = {
          name: mistake.pillarId,
          mistakes: []
        };
      }
      grouped[mistake.pillarId].mistakes.push(mistake);
    }

    return grouped;
  }
  /**
   * Mark mistake as reviewed
   */
  async markMistakeReviewed(userId, level, mistakeId) {
    try {
      const progressRef = doc(db, 'users', userId, 'grammarProgress', level.toLowerCase());

      await updateDoc(progressRef, {
        [`mistakes.details.${mistakeId}.reviewed`]: true,
        [`mistakes.details.${mistakeId}.reviewCount`]: increment(1),
        [`mistakes.details.${mistakeId}.lastReviewed`]: serverTimestamp()
      });

      return true;
    } catch (error) {
      console.error('Error marking mistake as reviewed:', error);
      return false;
    }
  }

  /**
   * Clear all mistakes for a user
   */
  async clearMistakes(userId, level) {
    try {
      const progressRef = doc(db, 'users', userId, 'grammarProgress', level.toLowerCase());

      await updateDoc(progressRef, {
        'mistakes.exercises': [],
        'mistakes.details': {},
        'mistakes.reviewQueue': []
      });

      return true;
    } catch (error) {
      console.error('Error clearing mistakes:', error);
      return false;
    }
  }

  /**
   * Get continuation point for user
   */
  async getContinuationPoint(userId, level) {
    try {
      const progress = await this.getUserProgress(userId, level);

      if (!progress) {
        return null;
      }

      return {
        pillarId: progress.currentPillar,
        exerciseIndex: progress.currentExerciseIndex,
        lastAccessed: progress.lastAccessedAt
      };
    } catch (error) {
      console.error('Error getting continuation point:', error);
      return null;
    }
  }

  /**
   * Clear continuation point
   */
  async clearContinuationPoint(userId, level) {
    try {
      const progressRef = doc(db, 'users', userId, 'grammarProgress', level.toLowerCase());
      await updateDoc(progressRef, {
        currentPillar: null,
        currentExerciseIndex: 0
      });

      return true;
    } catch (error) {
      console.error('Error clearing continuation point:', error);
      return false;
    }
  }

  /**
   * Calculate overall progress percentage
   */
  async getOverallProgress(userId, level) {
    try {
      const progress = await this.getUserProgress(userId, level);

      if (!progress || !progress.stats) {
        return 0;
      }

      // If totalExercises is 0 or undefined, calculate it from the grammar data
      let totalExercises = progress.stats.totalExercises;

      if (!totalExercises || totalExercises === 0) {
        try {
          const levelLower = level.toLowerCase();
          const practiceResponse = await fetch(`/data/grammar/grammar-${levelLower}.json`);
          if (practiceResponse.ok) {
            const practiceData = await practiceResponse.json();
            totalExercises = practiceData.exercises?.length || 0;

            // Update the progress document with the correct total
            const progressRef = doc(db, 'users', userId, 'grammarProgress', level.toLowerCase());
            await updateDoc(progressRef, {
              'stats.totalExercises': totalExercises
            });
          }
        } catch (e) {
          console.error('Could not fetch total exercises:', e);
          return 0;
        }
      }

      if (totalExercises === 0) {
        return 0;
      }

      const completedExercises = progress.stats.completedExercises || 0;
      const percentage = Math.round((completedExercises / totalExercises) * 100);

      return percentage;
    } catch (error) {
      console.error('Error calculating overall progress:', error);
      return 0;
    }
  }

  /**
   * Get weak areas based on mistakes
   */
  async getWeakAreas(userId, level, limit = 3) {
    try {
      const progress = await this.getUserProgress(userId, level);

      if (!progress || !progress.mistakes) {
        return [];
      }

      const mistakesByPillar = {};
      progress.mistakes.exercises.forEach(mistakeId => {
        const pillarId = mistakeId.split('_')[0];
        mistakesByPillar[pillarId] = (mistakesByPillar[pillarId] || 0) + 1;
      });

      return Object.entries(mistakesByPillar)
        .sort((a, b) => b[1] - a[1])
        .slice(0, limit)
        .map(([pillarId, count]) => ({
          pillarId,
          mistakeCount: count
        }));
    } catch (error) {
      console.error('Error getting weak areas:', error);
      return [];
    }
  }
}

const grammarProgressService = new GrammarProgressService();
export default grammarProgressService;